import React from "react";
import { Link } from "react-router-dom";
import { VideoCamera, ShieldCheck, Eye, CloudArrowUp, DeviceMobile, Fingerprint, Bell, ArrowRight } from "@phosphor-icons/react";
import { IMAGES } from "@/data/site";
import LeadForm from "@/components/LeadForm";

const SERVICES = [
  { t: "Home Security", d: "Doorbell cams, indoor & outdoor monitoring." },
  { t: "Office Security", d: "Multi-zone surveillance with employee access." },
  { t: "Factory Surveillance", d: "24×7 perimeter + production-floor coverage." },
  { t: "School Monitoring", d: "Safety-first child & classroom monitoring." },
];

const PRODUCTS = [
  { t: "2MP Cameras", d: "Entry-level HD, ideal for small homes" },
  { t: "5MP Cameras", d: "Ultra-clear, license-plate readable" },
  { t: "IP Cameras", d: "PoE network cameras with cloud" },
  { t: "PTZ Cameras", d: "Pan-Tilt-Zoom, 360° coverage" },
  { t: "NVR Systems", d: "4/8/16/32 channel network recorders" },
];

const BENEFITS = [
  { icon: Eye, t: "24×7 Monitoring", d: "Never miss a second." },
  { icon: DeviceMobile, t: "Mobile App Access", d: "Live feed on your phone." },
  { icon: CloudArrowUp, t: "Cloud Backup", d: "Footage safe even if DVR is stolen." },
  { icon: Bell, t: "Motion Detection", d: "Instant alerts for activity." },
];

export default function CCTV() {
  return (
    <div data-testid="cctv-page">
      <section className="relative bg-slate-950 text-white overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.cctv} alt="" className="w-full h-full object-cover opacity-50" />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/80 to-slate-950/30" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-28">
          <div className="label-eyebrow text-amber-400">Security Division</div>
          <h1 className="mt-4 font-heading font-black text-5xl md:text-7xl tracking-tighter max-w-3xl">
            Advanced security. <span className="italic font-light">Total peace of mind.</span>
          </h1>
          <p className="mt-6 max-w-xl text-lg text-slate-300">Hikvision, CP Plus &amp; Dahua-certified. From 2-camera homes to 64-channel factories.</p>
        </div>
      </section>

      <section className="bg-white py-20" data-testid="cctv-services-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="label-eyebrow">CCTV Services</div>
          <h2 className="mt-3 font-heading font-bold text-4xl md:text-5xl tracking-tighter text-slate-950">For every space.</h2>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200 border border-slate-200">
            {SERVICES.map((s, i) => (
              <div key={i} className="bg-white p-7" data-testid={`cctv-service-${i}`}>
                <VideoCamera size={28} weight="duotone" className="text-slate-900" />
                <h3 className="mt-5 font-heading font-bold text-xl text-slate-950">{s.t}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-20" data-testid="cctv-products-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="label-eyebrow">Products</div>
          <h2 className="mt-3 font-heading font-bold text-4xl md:text-5xl tracking-tighter text-slate-950">Tier-1 hardware only.</h2>
          <div className="mt-12 grid grid-cols-2 md:grid-cols-5 gap-6">
            {PRODUCTS.map((p, i) => (
              <div key={i} className="card-flat p-6" data-testid={`cctv-product-${i}`}>
                <div className="font-heading font-bold text-lg text-slate-950">{p.t}</div>
                <div className="mt-2 text-sm text-slate-600">{p.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-20" data-testid="cctv-smart-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="label-eyebrow">Smart Security</div>
            <h2 className="mt-3 font-heading font-bold text-4xl md:text-5xl tracking-tighter text-slate-950">Beyond cameras.</h2>
            <p className="mt-5 text-slate-600 leading-relaxed">Integrate access control, biometrics &amp; video door-phones with your CCTV stack for a single security ecosystem.</p>
            <div className="mt-8 grid grid-cols-3 gap-px bg-slate-200 border border-slate-200">
              <div className="bg-white p-5 text-center" data-testid="smart-access"><ShieldCheck size={28} weight="duotone" className="mx-auto text-amber-500" /><div className="mt-3 text-xs font-semibold uppercase tracking-wider">Access Control</div></div>
              <div className="bg-white p-5 text-center" data-testid="smart-biometric"><Fingerprint size={28} weight="duotone" className="mx-auto text-emerald-500" /><div className="mt-3 text-xs font-semibold uppercase tracking-wider">Biometric</div></div>
              <div className="bg-white p-5 text-center" data-testid="smart-doorphone"><Bell size={28} weight="duotone" className="mx-auto text-blue-500" /><div className="mt-3 text-xs font-semibold uppercase tracking-wider">Door Phone</div></div>
            </div>
          </div>
          <div className="card-flat overflow-hidden">
            <img src={IMAGES.cctv} alt="" className="w-full h-[460px] object-cover" />
          </div>
        </div>
      </section>

      <section className="bg-slate-950 text-white py-20" data-testid="cctv-benefits-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="label-eyebrow text-amber-400">Benefits</div>
          <h2 className="mt-3 font-heading font-bold text-4xl md:text-5xl tracking-tighter">Why customers love it.</h2>
          <div className="mt-12 grid md:grid-cols-4 gap-px bg-slate-800 border border-slate-800">
            {BENEFITS.map((b, i) => (
              <div key={i} className="bg-slate-950 p-7" data-testid={`cctv-benefit-${i}`}>
                <b.icon size={32} weight="duotone" className="text-amber-400" />
                <h4 className="mt-5 font-heading font-bold text-lg">{b.t}</h4>
                <p className="mt-2 text-sm text-slate-400">{b.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-5 lg:px-8"><LeadForm source="cctv" /></div>
      </section>
    </div>
  );
}
